import React, { useState, useEffect, useMemo } from "react";
import { Plus, Edit2, Trash2, DollarSign } from "lucide-react";
import { Pagination } from "../components/common/Pagination";
import { SearchInput } from "../components/ui/SearchInput";
import { commissionTypesService } from "../api/services/commissionTypes/commissionTypesService";
import {
  CommissionType,
  CreateCommissionTypeRequest,
} from "../api/services/commissionTypes/types";
import PrimaryButton from "../components/ui/PrimaryButton";
import toast from "react-hot-toast";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";

export const CommissionTypes: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [commissionTypes, setCommissionTypes] = useState<CommissionType[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState<CommissionType | null>(null);
  const [deleteType, setDeleteType] = useState<CommissionType | null>(null);
  const [formData, setFormData] = useState<CreateCommissionTypeRequest>({
    name: "",
    description: "",
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const itemsPerPage = 10;

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch commission types
  useEffect(() => {
    fetchCommissionTypes();
  }, [currentPage, debouncedSearchTerm]);

  const fetchCommissionTypes = async () => {
    setLoading(true);
    try {
      const response = await commissionTypesService.getCommissionTypes({
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
        name: debouncedSearchTerm || undefined,
      });
      setCommissionTypes(response.data.items || []);
      setTotalCount(response.data.pagination.total || 0);
    } catch (error) {
      console.error("Error fetching commission types:", error);
      toast.error("Failed to load commission types");
    } finally {
      setLoading(false);
    }
  };

  const totalPages = useMemo(() => {
    return Math.ceil(totalCount / itemsPerPage) || 1;
  }, [totalCount]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = "Commission type name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateOrUpdate = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      if (editingType) {
        await commissionTypesService.updateCommissionType(
          editingType.id,
          formData,
        );
        toast.success("Commission type updated successfully");
      } else {
        await commissionTypesService.createCommissionType(formData);
        toast.success("Commission type created successfully");
      }
      setShowModal(false);
      setEditingType(null);
      setFormData({
        name: "",
        description: "",
      });
      setErrors({});
      fetchCommissionTypes();
    } catch (error) {
      console.error("Error saving commission type:", error);
      toast.error(
        editingType
          ? "Failed to update commission type"
          : "Failed to create commission type",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteType) return;

    setLoading(true);
    try {
      await commissionTypesService.deleteCommissionType(deleteType.id);
      toast.success("Commission type deleted successfully");
      setDeleteType(null);
      fetchCommissionTypes();
    } catch (error) {
      console.error("Error deleting commission type:", error);
      toast.error("Failed to delete commission type");
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (type: CommissionType) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      description: type.description || "",
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingType(null);
    setFormData({
      name: "",
      description: "",
    });
    setErrors({});
    setShowModal(true);
  };

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">Commission Types</h1>
          <p className="text-slate-400 mt-1">
            Manage different types of commissions for players and agents
          </p>
        </div>

        {/* Search and Actions */}
        <div className="bg-slate-800 rounded-lg p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <SearchInput
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Search commission types..."
              />
            </div>
            <PrimaryButton onClick={openCreateModal}>
              <Plus className="w-4 h-4 mr-2" />
              Add Commission Type
            </PrimaryButton>
          </div>
        </div>

        {/* Commission Types Table */}
        <div className="bg-slate-800 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
                  </td>
                </tr>
              ) : commissionTypes.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-12 text-center text-slate-400"
                  >
                    {searchTerm
                      ? "No commission types found matching your search"
                      : "No commission types created yet"}
                  </td>
                </tr>
              ) : (
                commissionTypes.map((type) => (
                  <tr
                    key={type.id}
                    className="hover:bg-slate-700/50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <DollarSign className="w-4 h-4 text-blue-400 mr-2" />
                        <span className="text-white font-medium">
                          {type.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-slate-400 text-sm">
                        {type.description || "-"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          type.isActive
                            ? "bg-green-500/20 text-green-400"
                            : "bg-red-500/20 text-red-400"
                        }`}
                      >
                        {type.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => openEditModal(type)}
                        className="text-blue-400 hover:text-blue-300 mr-3"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteType(type)}
                        className="text-red-400 hover:text-red-300"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}

        {/* Results info */}
        {totalCount > 0 && (
          <div className="mt-4 text-sm text-slate-400 text-center">
            Showing {Math.min(itemsPerPage * currentPage, totalCount)} of{" "}
            {totalCount} commission types
            {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 rounded-lg max-w-md w-full">
            <div className="p-6 border-b border-slate-700">
              <h2 className="text-lg font-semibold text-white">
                {editingType
                  ? "Edit Commission Type"
                  : "Create New Commission Type"}
              </h2>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Commission Type Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                    if (errors.name) {
                      const newErrors = { ...errors };
                      delete newErrors.name;
                      setErrors(newErrors);
                    }
                  }}
                  className={`w-full px-3 py-2 bg-slate-700 border rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 ${
                    errors.name ? "border-red-500" : "border-slate-600"
                  }`}
                  placeholder="e.g., Rolling Commission, Loss Commission"
                />
                {errors.name && (
                  <p className="text-red-400 text-sm mt-1">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={3}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Optional description..."
                />
              </div>
            </div>

            <div className="flex gap-3 p-6 border-t border-slate-700">
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingType(null);
                  setErrors({});
                }}
                className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 hover:bg-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateOrUpdate}
                disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? "Saving..." : editingType ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteType && (
        <ConfirmDialog
          open={true}
          onConfirm={handleDelete}
          onCancel={() => setDeleteType(null)}
          title="Delete Commission Type"
          description={`Are you sure you want to delete "${deleteType.name}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
        />
      )}
    </div>
  );
};
